const UTM_KEY = 'river_utm_params';

const UTM_PARAM_NAMES = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

export interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  first_landing_page: string;
  referrer?: string;
  captured_at: string;
}

/**
 * Normalize a pathname for storage: collapse a trailing slash so that
 * "/login" and "/login/" are recorded as the same path. The root "/" itself
 * is preserved.
 */
function normalizeLandingPath(pathname: string): string {
  if (!pathname) return pathname;
  if (pathname === '/') return pathname;
  return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

/**
 * Pages that should never claim signup attribution: auth pages, admin shell,
 * logout. A user transiting through `/signup` after starting on `/` did not
 * "land" on `/signup` — that's the conversion destination, not the entry
 * point.
 */
function isExcludedFromAttribution(pathname: string): boolean {
  return (
    pathname === '/signup' || pathname.startsWith('/signup/') ||
    pathname === '/login' || pathname.startsWith('/login/') ||
    pathname === '/logout' || pathname.startsWith('/logout/') ||
    pathname === '/admin' || pathname.startsWith('/admin/') ||
    pathname.startsWith('/auth/') || pathname === '/auth'
  );
}

/**
 * Capture the first landing page (and UTM params if present) on first visit.
 * First-touch-wins: never overwrites an existing capture.
 * Always fires — even without UTM params — so we record the entry point for
 * organic, direct, and referral traffic too.
 */
export function captureUtmParams(): void {
  try {
    if (localStorage.getItem(UTM_KEY)) return;

    const params = new URLSearchParams(window.location.search);

    const captured: UtmParams = {
      first_landing_page: normalizeLandingPath(window.location.pathname),
      captured_at: new Date().toISOString(),
    };

    for (const key of UTM_PARAM_NAMES) {
      const val = params.get(key);
      if (val) captured[key] = val;
    }

    const ref = document.referrer;
    if (ref) captured.referrer = ref;

    localStorage.setItem(UTM_KEY, JSON.stringify(captured));
    console.info(`[UTM] Captured: utm_source=${captured.utm_source ?? '(none)'} referrer=${captured.referrer ?? '(none)'} landing_page=${captured.first_landing_page}`);
  } catch {
    // localStorage unavailable (private browsing edge cases) — silently skip
  }
}

/**
 * Upgrade the stored landing page when the initial capture was the root path.
 *
 * Rule: any pathname other than `/` is "dedicated". If we initially captured
 * `/` (the user opened the homepage first) and they then navigate to a
 * dedicated landing page before signing up, we overwrite `first_landing_page`
 * to the more-specific path. Once a dedicated path is stored we never
 * overwrite it — first dedicated wins.
 *
 * Auth/admin/logout paths are excluded: they're conversion destinations, not
 * landing pages, and must not claim attribution from the real entry point.
 */
export function upgradeLandingPageIfDedicated(pathname: string): void {
  try {
    if (!pathname || pathname === '/') return;
    if (isExcludedFromAttribution(pathname)) return;
    const stored = localStorage.getItem(UTM_KEY);
    if (!stored) return;
    const parsed = JSON.parse(stored) as UtmParams;
    if (parsed.first_landing_page !== '/') return;
    const normalized = normalizeLandingPath(pathname);
    parsed.first_landing_page = normalized;
    localStorage.setItem(UTM_KEY, JSON.stringify(parsed));
    console.info(`[UTM] Upgraded landing page from / to ${normalized}`);
  } catch {
    // localStorage unavailable or JSON malformed — silently skip.
  }
}

/**
 * Return the stored UTM params, or null if none were captured.
 */
export function getStoredUtmParams(): UtmParams | null {
  try {
    const stored = localStorage.getItem(UTM_KEY);
    return stored ? (JSON.parse(stored) as UtmParams) : null;
  } catch {
    return null;
  }
}

/**
 * Clear stored UTM params. Call after confirmed successful write to backend.
 */
export function clearUtmParams(): void {
  try {
    localStorage.removeItem(UTM_KEY);
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Affiliate ref code capture — first-touch-wins, parallel to UTM params
// ---------------------------------------------------------------------------

const REF_KEY = 'river_ref_code';

/**
 * Capture ?ref= query param on first visit (first-touch-wins, never overwrites).
 * Also called by the /r/:code route component which injects the code directly.
 */
export function captureRefCode(explicitCode?: string): void {
  try {
    if (localStorage.getItem(REF_KEY)) return;
    const code = explicitCode ?? new URLSearchParams(window.location.search).get('ref');
    if (code) {
      const normalized = code.toUpperCase().trim();
      localStorage.setItem(REF_KEY, normalized);
      console.info(`[Affiliate] Captured ref code: ${normalized}`);
    }
  } catch {
    // localStorage unavailable (private browsing edge cases) — silently skip
  }
}

/**
 * Return the stored affiliate ref code, or null if none was captured.
 */
export function getStoredRefCode(): string | null {
  try {
    return localStorage.getItem(REF_KEY);
  } catch {
    return null;
  }
}

/**
 * Clear stored affiliate ref code. Call after conversion is confirmed.
 */
export function clearRefCode(): void {
  try {
    localStorage.removeItem(REF_KEY);
  } catch {
    // ignore
  }
}
